import 'katex/dist/katex.min.css';
// Registers the self-hosted fallback faces (Comic Neue, Jost) for the ribbon's
// font picker everywhere slides render (editor canvas, present mode,
// thumbnails) — see `slideFonts.ts`.
import './slideFonts';
import {PAI_PALETTE} from 'utils/palette';
import {SLIDE_PT_TO_PX} from 'utils/slideUnits';
import {
  DEFAULT_MASTER_BACKGROUND_COLOR,
  DEFAULT_MASTER_ELEMENT_COLOR,
} from '../schema/deck';

/**
 * Slide visual theme constants — kept in one place so the editor canvas and
 * present mode always render identically.
 *
 * `SLIDE_FONT_FAMILY` leads with `KaTeX_Main`: a metrically-compatible
 * digitization of Computer Modern (via the Latin Modern project), the
 * classic LaTeX/TeX font. This is the same font family Manim's `Tex`-rendered
 * text uses by default, which is what gives 3Blue1Brown-style content its
 * distinctive look. KaTeX is already a dependency of this webapp (used by
 * the TipTap math extensions elsewhere) — importing its stylesheet here
 * registers the `@font-face` rules for `KaTeX_Main` regardless of whether
 * the user has navigated through a KaTeX-rendering page first.
 */
export const SLIDE_FONT_FAMILY =
  "'KaTeX_Main', Georgia, 'Times New Roman', serif";

/**
 * The monospace font stack behind the "Code" option in
 * `editor/FontFamilyControl.tsx`'s font picker (Menlo — the macOS system
 * monospace/code face — with Windows/Linux equivalents as fallbacks). Kept
 * here, not just inlined in the picker's option list, so other code that
 * needs to recognize "is this run in the Code font" (e.g. `shared/SmartQuotes.ts`,
 * which skips its straight-to-curly quote substitution for monospace/code
 * text) compares against the same string rather than a duplicated literal.
 */
export const SLIDE_MONOSPACE_FONT_FAMILY =
  "Menlo, Monaco, Consolas, 'Droid Sans Mono', 'Courier New', monospace";

/**
 * Default slide canvas background color. The actual background is now set per
 * deck by its slide master (`SlideMasterSchema.backgroundColor`); this alias
 * points at the value a new/migrated master starts with, kept so comments and
 * any legacy references resolve to a single source of truth in `schema/deck.ts`.
 */
export const SLIDE_BACKGROUND_COLOR = DEFAULT_MASTER_BACKGROUND_COLOR;

/**
 * Default color for slide elements (text, shape/table outlines) when neither
 * the element nor the deck's master specifies one. The live default is now set
 * per deck by its slide master (`SlideMasterSchema.elementColor`) and delivered
 * through `shared/slideElementColor.tsx`'s context; this alias points at the
 * value a new/migrated master starts with, kept so any render path outside that
 * context (and comments) resolves to a single source of truth in
 * `schema/deck.ts`.
 */
export const SLIDE_TEXT_COLOR = DEFAULT_MASTER_ELEMENT_COLOR;

/**
 * Swatch grid the slides editor's `ColorPicker` (see `editor/ColorPicker.tsx`)
 * offers for text/equation color — the slides feature's own palette, passed
 * in explicitly via `Toolbar.tsx` rather than relying on `ColorPicker`'s
 * generic fallback default. Keeping it here (not inside `ColorPicker.tsx`)
 * means a different embedder of that component can pass its own
 * `presetColors` without touching this one.
 *
 * Derived from the shared `PAI_PALETTE` (its insertion order — neutrals first,
 * then accents — is the swatch order) so the swatch grid and the KaTeX color
 * macros (`utils/katexMacros.ts`) stay in sync from one source of truth. Add
 * or retune a color in `utils/palette.ts`, not here.
 */
export const SLIDE_COLOR_SWATCHES: string[] = Object.values(PAI_PALETTE);

/**
 * Canvas px per slide **point** — the multiplier converting a `fontSize`
 * value (stored in slide points, exactly like PowerPoint font sizes — see
 * `schema/deck.ts`) into the CSS `font-size` that renders it on the
 * 1920×1080 canvas. Not an aesthetic fudge factor: the canvas is a
 * 13.33in × 7.5in slide at 144 dpi, and 1pt = 1/72in, so the conversion is
 * exactly 2. The canonical definition — and the full unit story, including
 * how demo-internal text speaks the same unit via `slidePt()` — lives in
 * `utils/slideUnits.ts`; this alias keeps the slide renderer's historical
 * name for it.
 */
export const FONT_SIZE_RENDER_SCALE = SLIDE_PT_TO_PX;

/**
 * The font-size multiplier KaTeX's own stylesheet applies at every `.katex`
 * root (`.katex { font: normal 1.21em … }` in `katex.min.css`) — KaTeX's
 * compensation for Computer Modern looking small next to typical surrounding
 * serif text. Keep in sync with the imported KaTeX stylesheet.
 */
export const KATEX_ROOT_FONT_EM = 1.21;

/**
 * The CSS `font-size`, in canvas px, for a standalone equation container
 * (equation and morphing-text elements) claiming `fontSizePt` slide points.
 *
 * Not simply `fontSizePt * FONT_SIZE_RENDER_SCALE`: KaTeX multiplies the
 * container size by {@link KATEX_ROOT_FONT_EM} at the `.katex` root, so an
 * uncompensated container renders glyphs 21% larger than the claimed point
 * size — visibly bigger than a text element at the same setting (both are
 * `KaTeX_Main`, so at equal claimed sizes they should match; inline math
 * inside text pins `.katex` to `1em` for the same reason, see
 * `shared/InlineMath.tsx`). Dividing the factor back out here makes the
 * rendered glyphs land at exactly `fontSizePt * FONT_SIZE_RENDER_SCALE` px.
 *
 * Every place that renders or measures a standalone equation MUST use this
 * (display: `shared/ElementContent.tsx`; glyph measurement:
 * `shared/glyphTokens.ts`; export raster: `export/rasterizeLatex.ts`) — a
 * mismatch between display and measurement reads as a jump when a glyph
 * transition settles.
 *
 * @param fontSizePt - The element's `fontSize` in slide points (see
 *     `schema/deck.ts`).
 * @return The container CSS font-size in canvas px.
 * @throws Never.
 */
export function equationFontSizePx(fontSizePt: number): number {
  return (fontSizePt * FONT_SIZE_RENDER_SCALE) / KATEX_ROOT_FONT_EM;
}

/**
 * Line-height for slide equation containers (equation and morphing-text
 * rendering in `shared/ElementContent.tsx`, and the glyph-measurement DOM in
 * `shared/glyphTokens.ts`'s `buildLatexMeasurementDom`). Pinned explicitly —
 * historically it was inherited from the app's global stylesheet, which made
 * measured glyph positions depend on what happened to be above the container
 * in the DOM; a mismatch here between measurement and display shows up as a
 * subtle jump the moment a glyph morph settles. The value matches the app
 * global it used to inherit, so pinning changed nothing visually.
 */
export const SLIDE_EQUATION_LINE_HEIGHT = 1.5;
